import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetCondition,
  AssetOwnership,
  AssetStatus,
  RepairStatus,
  WarrantyState,
} from '@cfl/assets-shared';

import { api } from '@/lib/api';
import { toQuery } from '@/lib/query';

export interface AssetSummary {
  id: string;
  code: string;
  categoryId: string;
  categoryName: string;
  name: string;
  brand: string | null;
  model: string | null;
  serial: string | null;
  status: AssetStatus;
  /** Company property, or the holder's own device. Only company property is chased
   *  at an exit - see isCompanyProperty in @cfl/assets-shared. */
  ownership: AssetOwnership;
  purchasedAt: string | null;
  price: string | null;
  warrantyEnd: string | null;
  warranty: WarrantyState;
  /** Where it physically is. Free text; usually null until someone walks the floor. */
  location: string | null;
  /** When somebody last confirmed it was actually there. Null means never. */
  lastVerifiedAt: string | null;
  holder: { id: string; employeeCode: string; name: string } | null;
  assignedAt: string | null;
  acknowledged: boolean;
  /** What went out with it. The return screen offers exactly these to tick back. */
  accessories: string[];
}

export interface AssetPage {
  assets: AssetSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AssetDetail extends Omit<AssetSummary, 'assignedAt' | 'acknowledged'> {
  vendor: string | null;
  invoiceNo: string | null;
  warrantyStart: string | null;
  /** Who recorded the last physical check. Null when it has never been verified. */
  lastVerifiedByName: string | null;
  notes: string | null;
  createdByName: string | null;
  createdAt: string;

  ramGb: number | null;
  cpu: string | null;
  storageGb: number | null;

  /** The asset's condition NOW. The frozen per-handover copies live on each
   *  assignment below - editing this can never rewrite what a handover said. */
  knownFaults: string[];
  conditionNotes: string | null;
  /** Null means nobody has ever recorded a condition. Not the same as "nothing
   *  is wrong with it" - see conditionVerdict. */
  conditionCheckedAt: string | null;

  assignments: Array<{
    id: string;
    employee: { id: string; employeeCode: string; name: string; department: string | null };
    assignedAt: string;
    condition: AssetCondition;
    remarks: string | null;
    accessories: string[];
    returnedAccessories: string[];
    assignedByName: string | null;
    acknowledgedAt: string | null;
    /** Set only when an administrator confirmed it for the holder. */
    acknowledgedByName: string | null;
    returnedAt: string | null;
    returnCondition: AssetCondition | null;
    returnReason: string | null;
    returnRemarks: string | null;
    returnedByName: string | null;

    /** What was already wrong with it when THIS person received it. */
    faultsAtHandover: string[];
    conditionNotesAtHandover: string | null;
    conditionCheckedAtHandover: string | null;

    /** The device login, if one was collected when it came back. The password is
     *  never in this payload - reading it is its own audited request. */
    deviceUsername: string | null;
    hasDeviceSecret: boolean;
    deviceSecretClearedAt: string | null;

    /** Photographed as it went out, and again as it came back. */
    handoverPhotos: Array<{ id: string; filename: string; mimeType: string }>;
    returnPhotos: Array<{ id: string; filename: string; mimeType: string }>;
  }>;
  repairs: Array<{
    id: string;
    startedAt: string;
    completedAt: string | null;
    status: RepairStatus;
    reason: string;
    problem: string | null;
    vendor: string | null;
    cost: string | null;
    parts: string | null;
    remarks: string | null;
  }>;
}

export interface AssetSummaryCounts {
  total: number;
  available: number;
  assigned: number;
  underRepair: number;
  damaged: number;
  lost: number;
  archived: number;
  unacknowledged: number;
  warrantyExpiringSoon: number;
  /** In service, but nobody has physically confirmed it inside the window. */
  verificationDue: number;
}

export interface AssetCategory {
  id: string;
  name: string;
  sortOrder: number;
  /** Whether returning one of these must record the device's local login. */
  requiresCredentials: boolean;
  /** Register data - absent for a caller without `viewRegister`, who still needs the names. */
  _count?: { assets: number };
}

/** Mirrors listAssetsSchema in the API - the only columns it can order by. */
export type AssetSort =
  | 'code'
  | 'name'
  | 'category'
  | 'serial'
  | 'status'
  | 'ownership'
  | 'holder'
  | 'acknowledged'
  | 'warrantyEnd'
  | 'purchasedAt'
  | 'createdAt';

export interface AssetFilters {
  search?: string;
  /** Exactly these assets, comma-separated. Used by the label sheet. */
  ids?: string;
  status?: AssetStatus | '';
  ownership?: AssetOwnership | '';
  categoryId?: string;
  holderId?: string;
  warranty?: 'EXPIRED' | 'EXPIRING_SOON' | '';
  /** Never physically verified, or not inside STOCK_TAKE_WINDOW_DAYS. */
  verification?: 'DUE' | '';
  includeArchived?: boolean;
  page?: number;
  pageSize?: number;
  sort?: AssetSort;
  direction?: 'asc' | 'desc';
}

export const assetKeys = {
  all: ['assets'] as const,
  list: (filters: AssetFilters) => ['assets', 'list', filters] as const,
  detail: (id: string) => ['assets', 'detail', id] as const,
  summary: ['assets', 'summary'] as const,
  categories: ['assets', 'categories'] as const,
};

/**
 * `enabled` mirrors useEmployees, and exists for the same reason: a dialog opened
 * against ONE known asset has nothing to pick, so it must not pull a thousand rows to
 * populate a picker it will not render.
 */
export function useAssets(filters: AssetFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: assetKeys.list(filters),
    queryFn: () => api.get<AssetPage>(`/assets${toQuery(filters)}`),
    // Keeps the previous page on screen while the next one loads, so paging and
    // typing in the search box do not blank the table on every keystroke.
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

export function useAsset(id: string | null) {
  return useQuery({
    queryKey: assetKeys.detail(id ?? ''),
    queryFn: () => api.get<AssetDetail>(`/assets/${id}`),
    enabled: id !== null,
  });
}

export function useAssetSummary() {
  return useQuery({
    queryKey: assetKeys.summary,
    queryFn: () => api.get<AssetSummaryCounts>('/assets/summary'),
  });
}

export function useAssetCategories() {
  return useQuery({
    queryKey: assetKeys.categories,
    queryFn: () => api.get<AssetCategory[]>('/assets/categories'),
    // Categories change a few times a year at most.
    staleTime: 5 * 60_000,
  });
}

/**
 * Brands and asset names already registered under this type, for the form's pickers.
 *
 * Keyed by category so choosing Laptop offers laptop brands and laptop names. Enabled
 * only once a type is picked - "every name in the company" is not a useful list to type
 * against, and it is what those fields looked like before.
 */
export function useAssetSuggestions(categoryId: string | null, brand?: string) {
  return useQuery({
    queryKey: [...assetKeys.all, 'suggestions', categoryId, brand ?? ''] as const,
    queryFn: () =>
      api.get<{ brands: string[]; names: string[]; models: string[]; locations: string[] }>(
        `/assets/suggestions?categoryId=${encodeURIComponent(categoryId!)}` +
          (brand ? `&brand=${encodeURIComponent(brand)}` : ''),
      ),
    enabled: categoryId !== null && categoryId !== '',
    staleTime: 5 * 60_000,
  });
}

/**
 * Locations already in use, across the whole register.
 *
 * Separate from useAssetSuggestions because that one is deliberately gated on a chosen
 * category - "every asset name in the company" is not a list worth typing against.
 * Locations are the opposite: a room holds laptops and chairs alike, so the list is
 * category-independent and is wanted on screens that have no category at all, like the
 * floor-walk dialog on the register.
 */
export function useLocationSuggestions() {
  return useQuery({
    queryKey: [...assetKeys.all, 'suggestions', 'locations'] as const,
    queryFn: () => api.get<{ locations: string[] }>('/assets/suggestions'),
    staleTime: 5 * 60_000,
  });
}

export interface AssetInput {
  categoryId?: string;
  categoryName?: string;
  name: string;
  brand?: string | null;
  model?: string | null;
  serial?: string | null;
  purchasedAt?: string | null;
  vendor?: string | null;
  invoiceNo?: string | null;
  price?: number | null;
  warrantyStart?: string | null;
  warrantyEnd?: string | null;
  status?: AssetStatus;
  ownership?: AssetOwnership;
  location?: string | null;
  notes?: string | null;

  ramGb?: number | null;
  cpu?: string | null;
  storageGb?: number | null;

  knownFaults?: string[];
  conditionNotes?: string | null;
  /**
   * "Somebody actually looked."
   *
   * Sent even when no faults were found, because that is the only way an empty list
   * can mean "checked, nothing wrong" rather than "nobody ever looked at this".
   */
  conditionChecked?: boolean;
}

/**
 * Turn the device-login requirement on or off for a whole asset type.
 *
 * Invalidates the asset namespace rather than just the category list: the return form
 * reads this flag to decide whether the login fields are required, so a stale copy
 * would let a laptop be returned without one.
 */
export function useSetCategoryCredentialRequirement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, requiresCredentials }: { id: string; requiresCredentials: boolean }) =>
      api.patch<{ id: string; requiresCredentials: boolean }>(`/assets/categories/${id}`, {
        requiresCredentials,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

export function useCreateAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssetInput) => api.post<AssetSummary>('/assets', input),
    // The whole namespace, not just the list: a new asset changes the dashboard
    // counts and may have created a category too.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

export function useUpdateAsset(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<AssetInput>) => api.patch<AssetSummary>(`/assets/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

/**
 * "I have just looked at these and they are here."
 *
 * Takes a list even for one asset, because the API does - a floor walk confirms a room
 * at a time, and the single button on the detail screen is that same call with one id.
 *
 * `location` is optional and applies to the whole batch: the reason to verify a room at
 * once is that everything in it is in the same room. Leave it undefined to keep each
 * asset's own location exactly as it is.
 */
export function useVerifyAssets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; location?: string }) =>
      api.post<{ verified: number; skipped: number }>('/assets/verify', input),
    // The whole namespace: this moves the dashboard's overdue count and the rows on
    // screen, and may have written a location the suggestion list should now offer.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

/** Resolve a sticker code to an id. What the QR deep link at /a/:code uses. */
export function useAssetByCode(code: string | null) {
  return useQuery({
    queryKey: [...assetKeys.all, 'by-code', code ?? ''] as const,
    queryFn: () => api.get<{ id: string }>(`/assets/by-code/${encodeURIComponent(code!)}`),
    enabled: code !== null && code !== '',
    // A sticker code never points somewhere else, so a failure is a wrong code rather
    // than a blip. Retrying just delays the message saying so.
    retry: false,
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assets/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; sortOrder?: number }) =>
      api.post<AssetCategory>('/assets/categories', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.categories }),
  });
}

/** Only ever succeeds for a type with no assets on it - the API is the one that decides. */
export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assets/categories/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assetKeys.categories }),
  });
}
